/**
 * Cầu nối tới delivery-api.
 *
 * Hai quy tắc chi phối file này:
 *
 * 1. Backend trả mã lỗi máy đọc được (`code`), KHÔNG trả chuỗi tiếng Việt
 *    (CLAUDE.md §6). Nên `ApiError` mang `code` và tầng UI mới dịch nó qua
 *    namespace `errors`.
 * 2. Token đi trong header `Authorization`, không bao giờ trong query string —
 *    URL bị ghi vào access log của mọi proxy trên đường đi.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** Khóa i18n trong namespace `errors`. */
    public readonly code: string,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'h5p-admin-token';
const REFRESH_KEY = 'h5p-admin-refresh';
const PROFILE_KEY = 'h5p-admin-profile';

export interface AdminProfile {
  principalId: string;
  tenantId: string;
  tenantName?: string | undefined;
  displayName: string;
  roles: string[];
}

/**
 * Token nằm ở `sessionStorage`, không phải `localStorage`.
 *
 * Khác biệt thật: sessionStorage chết theo tab, nên máy dùng chung ở trường
 * không để lại phiên đăng nhập cho người kế tiếp.
 */
export function getToken(): string {
  return window.sessionStorage.getItem(TOKEN_KEY) ?? '';
}

export function setToken(token: string): void {
  window.sessionStorage.setItem(TOKEN_KEY, token);
}

export function getRefreshToken(): string {
  return window.sessionStorage.getItem(REFRESH_KEY) ?? '';
}

export function getProfile(): AdminProfile | undefined {
  const raw = window.sessionStorage.getItem(PROFILE_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as AdminProfile;
  } catch {
    return undefined;
  }
}

export function saveSession(session: {
  accessToken: string;
  refreshToken: string;
  profile?: AdminProfile;
}): void {
  window.sessionStorage.setItem(TOKEN_KEY, session.accessToken);
  window.sessionStorage.setItem(REFRESH_KEY, session.refreshToken);
  if (session.profile) {
    window.sessionStorage.setItem(PROFILE_KEY, JSON.stringify(session.profile));
  }
}

const SELECTED_TENANT_KEY = 'h5p-admin-selected-tenant';

export function getSelectedTenant(): string | undefined {
  return window.sessionStorage.getItem(SELECTED_TENANT_KEY) ?? undefined;
}

export function setSelectedTenant(tenantId: string | undefined): void {
  if (!tenantId) {
    window.sessionStorage.removeItem(SELECTED_TENANT_KEY);
  } else {
    window.sessionStorage.setItem(SELECTED_TENANT_KEY, tenantId);
  }
}

export function clearToken(): void {
  window.sessionStorage.removeItem(TOKEN_KEY);
  window.sessionStorage.removeItem(REFRESH_KEY);
  window.sessionStorage.removeItem(PROFILE_KEY);
  window.sessionStorage.removeItem(SELECTED_TENANT_KEY);
}

function envUrl(key: string, fallback: string): string {
  const value = import.meta.env[key];
  return (typeof value === 'string' && value ? value : fallback).replace(/\/+$/, '');
}

function baseUrl(): string {
  return envUrl('VITE_DELIVERY_API_URL', 'http://localhost:3004');
}

function identityUrl(): string {
  return envUrl('VITE_IDENTITY_API_URL', 'http://localhost:3005');
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${baseUrl()}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    // Bỏ hẳn tham số rỗng thay vì gửi `key=`: backend phân biệt "không lọc"
    // với "lọc theo chuỗi rỗng", và cái sau luôn trả về không dòng nào.
    if (value === undefined || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  // Nếu system_admin đã chọn một tenant cụ thể và route không phải quản lý danh sách tenant,
  // tự động gắn tenantId vào query nếu chưa được truyền.
  const selected = getSelectedTenant();
  if (selected && !url.searchParams.has('tenantId') && !path.startsWith('/api/admin/tenants')) {
    const profile = getProfile();
    if (profile?.roles?.includes('system_admin')) {
      url.searchParams.set('tenantId', selected);
    }
  }

  return url.toString();
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => ({}))) as { code?: string };
  return new ApiError(response.status, body.code ?? 'api.error.internal');
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const hasBody = options.body !== undefined;
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken()}` };
  if (hasBody) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    // Mạng chết hoặc CORS chặn. Không phân biệt được từ trong trang — trình
    // duyệt cố tình không nói lý do — nên gộp thành một mã.
    throw new ApiError(0, 'api.error.coreUnavailable');
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Tải file (Excel, .h5p).
 *
 * Đi qua `fetch` chứ không mở thẳng URL trong tab mới, vì tab mới không mang
 * được header `Authorization`. Đổi lại phải tự dựng blob và tự kích hoạt tải.
 */
export async function apiDownload(
  path: string,
  options: RequestOptions & { fallbackFileName: string },
): Promise<void> {
  const hasBody = options.body !== undefined;
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken()}` };
  if (hasBody) headers['Content-Type'] = 'application/json';

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
  });
  if (!response.ok) throw await toApiError(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileNameFrom(response) ?? options.fallbackFileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Thu hồi ngay sau khi click: giữ URL sống là giữ cả blob trong bộ nhớ, và
  // vài lần tải báo cáo lớn là đủ để tab phình lên hàng trăm MB.
  URL.revokeObjectURL(url);
}

/**
 * Tên file từ `Content-Disposition`.
 *
 * Ưu tiên `filename*=UTF-8''…` theo RFC 5987 — chỉ dạng này mang được tên có
 * dấu tiếng Việt. `filename=` trần chỉ an toàn với ASCII, backend cố tình đặt
 * nó thành tên rút gọn không dấu.
 */
function fileNameFrom(response: Response): string | undefined {
  const header = response.headers.get('content-disposition');
  if (!header) return undefined;

  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // Tên mã hóa hỏng — rơi về dạng ASCII bên dưới thay vì làm hỏng cả lần tải.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1];
}

/**
 * Đăng nhập qua service identity.
 *
 * Trước đây màn hình này chỉ có một ô dán token — đủ để nghiệm thu Phase 5 và
 * cố tình thô để không ai nhầm với luồng thật. Giờ đã có identity nên thay
 * bằng đăng nhập đúng nghĩa.
 */
export interface AdminLoginResult {
  accessToken: string;
  refreshToken: string;
  principalId: string;
  tenantId: string;
  tenantName?: string | undefined;
  displayName: string;
  roles?: string[] | undefined;
}

export async function adminLogin(email: string, password: string): Promise<AdminLoginResult> {
  let response: Response;
  try {
    response = await fetch(`${identityUrl()}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new ApiError(0, 'api.error.coreUnavailable');
  }
  if (!response.ok) throw await toApiError(response);

  const result = (await response.json()) as AdminLoginResult;
  saveSession({
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    profile: {
      principalId: result.principalId,
      tenantId: result.tenantId,
      tenantName: result.tenantName,
      displayName: result.displayName,
      roles: result.roles ?? [],
    },
  });
  return result;
}

export async function adminLogout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    // Lỗi ở đây không chặn việc đăng xuất phía client: bấm "Thoát" thì phải
    // thoát, kể cả khi server không phản hồi.
    await fetch(`${identityUrl()}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => undefined);
  }
  clearToken();
}
